/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
/* eslint-disable sonarjs/cognitive-complexity */
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';

import { UpdateTaskScheduleInput, WorkOrderYesNoType } from '../../../../../API';
import WorkOrdersApiService from '../../../../Jobs/Services/WorkOrdersService';
import Button, { ButtonType } from '../../../../../Shared/Components/Common/Buttons/Button';
import SnackbarMessage from '../../../../../Shared/Components/Common/SnackbarMessage/SnackbarMessage';
import AdjustProofRequirementsPopup from './AdjustProofRequirementsPopup';
import PageTitle from '../../../../../Shared/Components/Common/PageTitle/PageTitle';

import theme from '../../../../../Shared/Themes/theme';
import { Icons } from '../../../../../Shared/Constants/Icons';

import {
  HeadingContainer,
  NoteText,
  ScheduleDaysText,
  TasksContainer,
  TextContainer,
  UpdateButtonContainer,
} from '../AllJobs.Style';
import { CustomerContainer } from '../../../../../Shared/Components/Layout/styles';
import { Loader } from '../../../../Jobs/Components/JobView/JobsView.styles';
import { ISelectedJob, IServiceData } from '../AllSchedules.Model';

const ModifyProofSettingsSpecificTasks = (): JSX.Element => {
  const [servicesData, setServicesData] = useState<IServiceData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [anyCheckboxChecked, setAnyCheckboxChecked] = useState(false);
  const [selectedOption, setSelectedOption] = useState<WorkOrderYesNoType | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [isProofPopupOpen, setIsProofPopupOpen] = useState(false);

  const location = useLocation();
  const { t } = useTranslation(['jobManagement']);
  const navigate = useNavigate();

  const fetchServices = () => {
    if (!location?.state?.selectedJobs || location?.state?.selectedJobs?.length === 0) return;
    setIsLoading(true);
    try {
      const jobs = location?.state?.selectedJobs as ISelectedJob[];
      const servicesFromState = location?.state?.servicesData as any[];

      const formattedServices = servicesFromState.map((service) => ({
        ...service,
        tasks: service?.tasks.map((task: any) => ({
          ...task,
          checked: false,
        })),
      }));

      const allServices = formattedServices.map((service) => {
        const job = jobs.find((j) => j.woScheduleMasterId === service.woScheduleMasterId);
        return {
          ...service,
          scheduleName: job?.scheduleName || '',
          jobName: job?.jobName || '',
        };
      });

      setServicesData(allServices);
    } catch (error) {
      console.error(t('jobManagement:errorFetchingServices'), error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTaskCheckboxChange = (serviceIndex: number, taskIndex: number) => {
    const updatedServices = [...servicesData];
    updatedServices[serviceIndex].tasks[taskIndex].checked = !updatedServices[serviceIndex].tasks[taskIndex].checked;
    setServicesData(updatedServices);
  };

  const handleConfirmSubmit = async () => {
    if (!selectedOption) return;

    setIsUpdating(true);
    try {
      const payload: UpdateTaskScheduleInput[] = [];
      servicesData?.forEach((service) => {
        service.tasks.forEach((task) => {
          if (task.checked) {
            payload.push({
              isProofOfCompletionRequired: selectedOption,
              taskScheduleId: task.woScheduleServiceId,
            });
          }
        });
      });

      const result = await WorkOrdersApiService.updateTaskSchedule(payload);
      if (result.errors && result.errors.length > 0) {
        setSnackbarMessage(t('jobManagement:updatedTaskProofFail'));
        setSnackbarOpen(true);
        setSnackbarSeverity('error');
      } else {
        const updatedServices = servicesData.map((service) => ({
          ...service,
          tasks: service.tasks.map((task) => ({
            ...task,
            isProofOfCompletionRequired: task.checked ? selectedOption : task.isProofOfCompletionRequired,
            checked: false,
          })),
        }));
        setServicesData(updatedServices);
        setSelectedOption(null);
        setSnackbarMessage(t('jobManagement:updatedTaskProofSuccess'));
        setSnackbarSeverity('success');
      }
      setSnackbarOpen(true);
      if (!result.errors || result.errors.length === 0) {
        setIsProofPopupOpen(false);
        setTimeout(() => {
          navigate('/all-schedules/modify-proof-settings');
        }, 1000);
      }
    } catch (error) {
      setSnackbarMessage(t('jobManagement:updatedTaskProofFail'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsUpdating(false);
    }
  };

  useEffect(() => {
    fetchServices();
  }, []);

  useEffect(() => {
    const hasChecked = servicesData.some((service) => service.tasks?.some((task) => task.checked));
    setAnyCheckboxChecked(hasChecked);
  }, [servicesData]);

  return (
    <>
      <PageTitle title={t('jobManagement:manageTaskProofRequirementForSpecificTasks')} />
      <CustomerContainer>
        {isLoading ? (
          <Loader sx={{ height: '100%' }}>
            <CircularProgress size={40} sx={{ color: theme.palette.primary.dark }} />
          </Loader>
        ) : (
          <>
            <TasksContainer>
              {servicesData.map((service, serviceIndex) => (
                <Box key={serviceIndex} sx={{ background: theme.palette.common.white, borderRadius: '1rem' }}>
                  <HeadingContainer>
                    <TextContainer sx={{ marginBottom: '0.5rem' }}>
                      <ScheduleDaysText
                        sx={{ marginBottom: 0, fontSize: theme.typography.h3?.fontSize, whiteSpace: 'nowrap' }}>
                        {t('jobManagement:jobName')}:&nbsp;
                      </ScheduleDaysText>
                      <NoteText sx={{ marginBottom: 0, fontSize: theme.typography.h3?.fontSize }}>
                        {service.jobName}
                      </NoteText>
                    </TextContainer>
                    <TextContainer>
                      <ScheduleDaysText
                        sx={{ marginBottom: 0, fontSize: theme.typography.h3?.fontSize, whiteSpace: 'nowrap' }}>
                        {t('jobManagement:scheduleName')}:&nbsp;
                      </ScheduleDaysText>
                      <NoteText sx={{ marginBottom: 0, fontSize: theme.typography.h3?.fontSize }}>
                        {service.scheduleName}
                      </NoteText>
                    </TextContainer>
                  </HeadingContainer>
                  <TextContainer>
                    <NoteText sx={{ padding: '1rem', marginBottom: 0, fontWeight: theme.typography.fontWeightMedium }}>
                      {t('tableHeader:serviceName')}:
                    </NoteText>
                    <NoteText sx={{ padding: '1rem 1rem 1rem 0', marginBottom: 0 }}>{service.mdServiceName}</NoteText>
                  </TextContainer>
                  <Box sx={{ paddingBottom: '1rem' }}>
                    <ScheduleDaysText sx={{ paddingLeft: '1rem' }}>{t('jobManagement:tasks')}:</ScheduleDaysText>
                    {service.tasks?.length > 0 ? (
                      <Box sx={{ paddingLeft: '1.5rem' }}>
                        {service.tasks.map((task, taskIndex) => (
                          <Box
                            key={taskIndex}
                            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                            onClick={() => handleTaskCheckboxChange(serviceIndex, taskIndex)}>
                            <img
                              src={task.checked ? Icons.CheckedIcon : Icons.Unchecked}
                              style={{ cursor: 'pointer', height: '1.5rem', width: '1.5rem' }}
                            />
                            <NoteText sx={{ margin: ' 0 0 0 0.5rem' }}>{task.mdTaskName} - </NoteText>
                            <NoteText
                              sx={{
                                margin: '0 0 0 0.5rem',
                                color:
                                  task.isProofOfCompletionRequired === 'Yes'
                                    ? theme.palette.error.main
                                    : theme.palette.success.main,
                              }}>
                              {task.isProofOfCompletionRequired === 'Yes'
                                ? t('jobManagement:proofMandatory')
                                : t('jobManagement:proofOptional')}
                            </NoteText>
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <NoteText>{t('jobManagement:noTasks')}</NoteText>
                    )}
                  </Box>
                </Box>
              ))}
            </TasksContainer>
            {anyCheckboxChecked && (
              <UpdateButtonContainer>
                <Button
                  type={ButtonType.Primary}
                  onClick={() => setIsProofPopupOpen(true)}
                  label={t('jobManagement:modifyProofSettings')}
                />
              </UpdateButtonContainer>
            )}
            <AdjustProofRequirementsPopup
              open={isProofPopupOpen}
              onClose={() => setIsProofPopupOpen(false)}
              onSubmit={handleConfirmSubmit}
              selectedOption={selectedOption}
              onOptionChange={setSelectedOption}
              isUpdating={isUpdating}
              title={t('jobManagement:modifyProofSettings')}
              options={[
                {
                  value: WorkOrderYesNoType.Yes,
                  label: t('jobManagement:changeAllTasksToProofRequired'),
                },
                {
                  value: WorkOrderYesNoType.No,
                  label: t('jobManagement:changeAllTasksToProofOptional'),
                },
              ]}
              cancelButtonText={t('assignJob:cancel')}
              confirmButtonText={t('dashboard:confirm')}
            />
            <SnackbarMessage
              open={snackbarOpen}
              successMessage={snackbarMessage}
              errorMessage={snackbarMessage}
              severity={snackbarSeverity}
              onClose={() => setSnackbarOpen(false)}
            />
          </>
        )}
      </CustomerContainer>
    </>
  );
};

export default ModifyProofSettingsSpecificTasks;
